<script setup>
import { Link } from '@inertiajs/vue3'
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import ToggleInput from '@/Shared/ToggleInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import Pagination from '@/Shared/Pagination.vue'
import { useForm, router } from '@inertiajs/vue3'
import { watch } from 'vue'
import { useDate } from '@/Composables/useDate'
import { useConfirm } from '@/Composables/useConfirm'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  coaches: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: null
  },
  lesson: {
    type: Object,
    required: true
  },
  levels: {
    type: Object,
    required: true
  },
  students: {
    type: Array,
    default: () => []
  },
  weekdays: {
    type: Array,
    default: () => []
  },
  records: {
    type: Object,
    required: true
  }
})

let form = useForm({
  coach_id: props.coaches[0].id,
  level_id: props.filters.level_id || props.lesson.level_id,
  time_start: props.lesson.time_start,
  time_end: props.lesson.time_end,
  weekdays: props.lesson.weekdays,
  students: props.students
    .filter((s) => props.lesson.students.map((st) => st.id).includes(s.id))
    .map((s) => s.id),
  enabled: props.lesson.enabled,
  date_start: useDate(props.lesson.date_start).format('YYYY-MM-DD'),
  date_end: useDate(props.lesson.date_end).format('YYYY-MM-DD'),
  max_students: props.lesson.max_students
})

watch(
  () => form.level_id,
  (level_id) => {
    form.students = []

    router.get(
      `/lessons/${props.lesson.id}/edit`,
      { level_id },
      {
        preserveState: true,
        preserveScroll: true
      }
    )
  }
)

watch(
  () => form.enabled,
  () => {
    router.put(`/lessons/${props.lesson.id}/enable`, {
      preserveState: true,
      preserveScroll: true
    })
  }
)

function update() {
  form.put(`/lessons/${props.lesson.id}/update`)
}

async function deleteLesson() {
  if (await useConfirm('¿Estás seguro de que quieres eliminar la clase?')) {
    router.delete(`/lessons/${props.lesson.id}/delete`)
  }
}

const breadcrumbs = [
  { label: 'Clases' },
  { label: 'Horarios', href: '/lessons' },
  {
    label: `${props.levels.find((l) => l.id === props.lesson.level_id).name} ${
      props.lesson.time_start
    } - ${props.lesson.time_end}`
  }
]
</script>

<template>
  <Portrait
    :title="`Clase: ${props.levels.find((l) => l.id === props.lesson.level_id).name} ${
      props.lesson.time_start
    } - ${props.lesson.time_end}`"
    subtitle="Gestiona la información y alumnos de la clase"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <div class="flex justify-end mb-6">
      <ToggleInput v-model="form.enabled" label="Activada" />
    </div>

    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6">
      <form class="space-y-6" @submit.prevent="update">
        <div class="grid grid-cols-1 gap-6">
          <div class="bg-gray-50 p-4 rounded-md">
            <h4 class="text-sm font-medium text-gray-700 mb-4">Información básica</h4>
            <div class="grid grid-cols-1 gap-4">
              <SelectInput
                v-model="form.coach_id"
                :error="form.errors.coach_id"
                class="w-full"
                label="Entrenador"
              >
                <option v-for="coach in coaches" :key="coach.id" :value="coach.id">
                  {{ coach.player.fullname }}
                </option>
              </SelectInput>

              <SelectInput
                v-model="form.level_id"
                :error="form.errors.level_id"
                class="w-full"
                label="Nivel"
              >
                <option v-for="level in levels" :key="level.id" :value="level.id">
                  {{ level.name }}
                </option>
              </SelectInput>

              <TextInput
                v-model="form.max_students"
                :error="form.errors.max_students"
                class="w-full"
                label="Número máximo de alumnos"
                type="number"
              />
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-md">
            <h4 class="text-sm font-medium text-gray-700 mb-4">Horario</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                v-model="form.time_start"
                :error="form.errors.time_start"
                type="time"
                class="w-full"
                label="Hora inicio"
              />
              <TextInput
                v-model="form.time_end"
                :error="form.errors.time_end"
                type="time"
                class="w-full"
                label="Hora fin"
              />
            </div>

            <div class="mt-4">
              <SelectInput
                v-model="form.weekdays"
                :error="form.errors.weekdays"
                class="w-full"
                label="Días de la semana"
                multiple
              >
                <option v-for="weekday in weekdays" :key="weekday.value" :value="weekday.value">
                  {{ weekday.name }}
                </option>
              </SelectInput>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <TextInput
                v-model="form.date_start"
                :error="form.errors.date_start"
                class="w-full"
                label="Fecha inicio"
                type="date"
              />
              <TextInput
                v-model="form.date_end"
                :error="form.errors.date_end"
                class="w-full"
                label="Fecha fin"
                type="date"
              />
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-md">
            <h4 class="text-sm font-medium text-gray-700 mb-4">Alumnos</h4>
            <SelectInput
              v-model="form.students"
              :error="form.errors.students"
              class="w-full"
              label="Selecciona alumnos"
              multiple
            >
              <option v-for="student in students" :key="student.id" :value="student.id">
                {{ student.player.fullname }}
              </option>
            </SelectInput>
          </div>
        </div>
      </form>
    </div>

    <div class="mt-8">
      <h2 class="text-lg font-medium text-gray-800 mb-4">Próximas Clases</h2>

      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <thead>
              <tr class="text-left font-medium text-gray-700 bg-gray-50 border-b border-gray-200">
                <th class="py-3 px-6">Día</th>
                <th class="py-3 px-6">Hora</th>
                <th class="py-3 px-6">Alumnos</th>
                <th class="py-3 px-6">Entrenador</th>
                <th class="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in records.data"
                :key="record.id"
                class="hover:bg-gray-50 focus-within:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <td class="py-3 px-6">
                  <Link
                    class="flex items-center text-gray-800 hover:text-indigo-600"
                    :href="`/lessons/records/${record.id}/edit`"
                    tabindex="-1"
                  >
                    {{ useDate(record.date_start).format('DD-MM-YYYY') }}
                  </Link>
                </td>
                <td class="py-3 px-6">
                  <Link
                    class="flex items-center text-gray-800 hover:text-indigo-600"
                    :href="`/lessons/records/${record.id}/edit`"
                    tabindex="-1"
                  >
                    {{ useDate(record.date_start).format('HH:mm') }} -
                    {{ useDate(record.date_end).format('HH:mm') }}
                  </Link>
                </td>
                <td class="py-3 px-6">
                  <Link
                    class="flex items-center text-gray-800 hover:text-indigo-600"
                    :href="`/lessons/records/${record.id}/edit`"
                    tabindex="-1"
                  >
                    {{ record.students.length }}
                  </Link>
                </td>
                <td class="py-3 px-6">
                  <Link
                    class="flex items-center text-gray-800 hover:text-indigo-600"
                    :href="`/lessons/records/${record.id}/edit`"
                    tabindex="-1"
                  >
                    {{ record.lesson.coach.player.fullname }}
                  </Link>
                </td>
                <td class="py-3 px-6 text-right">
                  <Link
                    class="inline-flex items-center text-gray-400 hover:text-indigo-600"
                    :href="`/lessons/records/${record.id}/edit`"
                    tabindex="-1"
                  >
                    <Icon name="CheveronRight" class="w-5 h-5" />
                  </Link>
                </td>
              </tr>
              <tr v-if="records.data.length === 0">
                <td class="px-6 py-4 text-center text-gray-500" colspan="5">
                  No hay registros todavía.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <Pagination class="mt-6" :links="records.links" :preserve-state="false" :only="['records']" />
    </div>

    <template #footer>
      <!-- Versión móvil -->
      <div class="w-full block sm:hidden">
        <div class="flex items-stretch justify-end w-full space-x-4">
          <button class="btn-red" type="button" @click="deleteLesson">Eliminar</button>

          <LoadingButton
            :loading="form.processing"
            class="btn-indigo"
            type="submit"
            @click="update"
          >
            Guardar
          </LoadingButton>
        </div>
      </div>

      <!-- Versión escritorio -->
      <div class="hidden sm:flex sm:items-center sm:justify-end w-full space-x-4">
        <button class="btn-red" type="button" @click="deleteLesson">Eliminar</button>

        <LoadingButton :loading="form.processing" class="btn-indigo" type="submit" @click="update">
          Guardar
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
